import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { ProductSliderComponent } from "@/components/Product/ProductSlider/productSlider.component";
import { ProductSliderSkeletonComponent } from "@/components/Product/ProductSlider/skeleton/productSliderSkeleton.component";
import {
    getRecentlyViewed,
    removeItemFromRecentlyViewed,
    removeRecentlyViewed
} from "@/services/recentlyViewedProducts.service";

import { useProductSliderContext } from "../productSlider.context";

export type RecentsProductSliderProps = {
    sku?: string;
};

const RecentsProductslider = ({ sku }: RecentsProductSliderProps) => {
    const { prefetchedProducts } = useProductSliderContext();
    const [products, setProducts] = useState<
        Array<Product<ProductFieldSet.ProductTile>> | undefined
    >(prefetchedProducts);

    const [{ loading, error }, loadProducts] = useBFFProductServiceApi<
        any,
        any,
        Array<Product<ProductFieldSet.ProductTile>>
    >(
        {
            url: `/products`,
            method: "GET"
        },
        {
            manual: true
        }
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isDefined(products)) return;
        if (loading || error) return;

        const recentlyViewedSkus = getRecentlyViewed(sku);

        if (isEmpty(recentlyViewedSkus)) {
            setProducts([]);
        } else {
            loadProducts({
                params: {
                    sku: recentlyViewedSkus.join(","),
                    fields: ProductFieldSet.ProductTile
                }
            }).then(({ data }) => {
                setProducts(data);
            });
        }
    }, [sku, loadProducts, setProducts]);

    const deleteFromHistory = (sku: string) => {
        removeItemFromRecentlyViewed(sku);
        setProducts((prevSkus) => prevSkus?.filter((item) => item.sku !== sku));
    };

    const deleteRecentsHistory = () => {
        removeRecentlyViewed();
        setProducts([]);
    };

    if (error) {
        return null;
    } else if (isDefined(products) && isEmpty(products)) {
        return null;
    } else if (isDefined(products) && !isEmpty(products)) {
        return (
            <ProductSliderComponent
                products={products}
                deleteFromHistory={deleteFromHistory}
                deleteRecentsHistory={deleteRecentsHistory}
            />
        );
    } else {
        return <ProductSliderSkeletonComponent />;
    }
};

export { RecentsProductslider };
